import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Drawer, Input, Button, message, Form, Row, Col } from 'antd';
import { CommentOutlined, CloseCircleFilled } from '@ant-design/icons';
import { Auth } from 'aws-amplify';
import API from '../../../api/API';
import { Comment, CommentLoading } from './index';
import { IComment } from '../../../models/comment';
import './CommentDrawer.css';

const { TextArea } = Input;

interface IProps {
  visible: boolean;
  onClose: () => void;
  eventId: string;
}

const CommentDrawer: React.FC<IProps> = (props: IProps) => {
  // #region Setting up Context, State, Props, etc.

  const [addComment, setAddComment] = useState(false);
  const [comments, setComments] = useState<IComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [form] = Form.useForm();
  const { visible, onClose, eventId } = props;

  // #endregion

  // #region Component Methods

  /*
   * Fetches the comments for this particular event from the API.
   * On completion, updates the comments and loading status in the
   * state.
   */
  const fetchComments = useCallback(async () => {
    const resp = await API.get(`/comment/${eventId}`);
    setComments(resp.data);
    setLoading(false);
    buttonRef.current!.scrollIntoView();
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      try {
        setLoading(true);
        setAddComment(false);
        fetchComments();
      } catch {
        setLoading(true);
        message.error('Looks like something went wrong.');
      }
    }
  }, [fetchComments, eventId, form]);

  const onCommentClick = () => {
    form.resetFields();
    setAddComment(!addComment);
  };

  /*
   * When a new comment is added from the add comment text box,
   * we send the new comment to the API and update the comment state.
   */
  const onFinish = async (values: any) => {
    try {
      setSubmitting(true);
      onCommentClick();

      const currentUser = await Auth.currentAuthenticatedUser();

      const newComment = {
        eventId,
        createdBy: currentUser.username,
        createdOn: new Date(),
        text: values.text,
      };

      await API.post(`/comment/${eventId}`, newComment);
      await fetchComments();
      setSubmitting(false);
    } catch {
      setSubmitting(false);
      message.error('Looks like something went wrong.');
    }
  };

  // #endregion

  // #region Render Section

  return (
    <Drawer
      width={300}
      title="Comments"
      placement="right"
      mask={false}
      visible={visible}
      onClose={onClose}
      destroyOnClose
      closeIcon={<CloseCircleFilled />}
    >
      {!loading ? (
        <>
          <Row>
            <Col span={24} className="CommentDrawer-comments">
              {comments.map((c: IComment) => (
                <Comment
                  comment={c}
                  fetchComments={fetchComments}
                  key={`comment:${c.id}`}
                />
              ))}
              {submitting && <CommentLoading />}
            </Col>
          </Row>
          {addComment ? (
            <Form form={form} onFinish={onFinish} size="large">
              <Form.Item
                name="text"
                rules={[
                  { required: true, message: 'Comment cannot be empty!' },
                  () => ({
                    validator(rule, value) {
                      if (!value || value.trim().length) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(`Comment cannot be empty!`),
                      );
                    },
                  }),
                ]}
              >
                <TextArea rows={3} placeholder="Add a comment..." />
              </Form.Item>
              <Button
                type="primary"
                icon={<CommentOutlined />}
                htmlType="submit"
                ref={buttonRef}
              >
                Comment
              </Button>
            </Form>
          ) : (
            <Button
              type="primary"
              onClick={onCommentClick}
              icon={<CommentOutlined />}
              size="large"
              ref={buttonRef}
            >
              Add Comment
            </Button>
          )}
        </>
      ) : (
        <CommentLoading />
      )}
    </Drawer>
  );

  // #endregion
};

export default CommentDrawer;
